/**
 * SaveStatusBadge — the single source of truth for "is my data safe?"
 *
 * Renders a compact pill in the Field Notes header that always reflects the
 * current save state coming out of `useFieldNotesSave`. Five visible states:
 *
 *   ● Saved to cloud · {time ago}                    [green]
 *   ● Saving…                                        [blue, pulse]
 *   ● Saved locally · server unreachable [Retry]     [amber]
 *   ● Sync pending · 1 change queued [Retry]         [amber]
 *   ● Sync failed · tap to retry [Retry] [Details]   [red]
 *
 * "Details" is gated behind the org's developer mode flag.
 */
import { Cloud, CloudOff, RotateCw, AlertTriangle, Loader2, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SAVE_STATUS } from '@/hooks/useFieldNotesSave';

const formatRelative = (date) => {
  if (!date) return '';
  const ms = Date.now() - new Date(date).getTime();
  if (ms < 5_000) return 'just now';
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  return `${Math.round(ms / 3_600_000)}h ago`;
};

const SaveStatusBadge = ({
  status,
  lastSavedAt,
  lastError,
  onRetry,
  developerMode = false,
}) => {
  const showRetry = (
    status === SAVE_STATUS.SAVED_LOCALLY ||
    status === SAVE_STATUS.PENDING_RETRY ||
    status === SAVE_STATUS.SYNC_FAILED
  );

  let icon, text, sub, tone;

  switch (status) {
    case SAVE_STATUS.SAVING:
      icon = <Loader2 className="w-3.5 h-3.5 animate-spin" />;
      text = 'Saving…';
      sub = '';
      tone = 'bg-blue-50 text-blue-700 border-blue-200';
      break;
    case SAVE_STATUS.SAVED:
      icon = <Check className="w-3.5 h-3.5" />;
      text = 'Saved to cloud';
      sub = lastSavedAt ? formatRelative(lastSavedAt) : '';
      tone = 'bg-emerald-50 text-emerald-700 border-emerald-200';
      break;
    case SAVE_STATUS.SAVED_LOCALLY: {
      icon = <CloudOff className="w-3.5 h-3.5" />;
      const isOffline = lastError?.kind === 'offline';
      text = isOffline ? 'Saved locally · offline' : 'Saved locally · server unreachable';
      sub = 'Will sync automatically';
      tone = 'bg-amber-50 text-amber-800 border-amber-200';
      break;
    }
    case SAVE_STATUS.PENDING_RETRY:
      icon = <RotateCw className="w-3.5 h-3.5" />;
      text = 'Sync pending';
      sub = 'Retrying in background';
      tone = 'bg-amber-50 text-amber-800 border-amber-200';
      break;
    case SAVE_STATUS.SYNC_FAILED:
      icon = <AlertTriangle className="w-3.5 h-3.5" />;
      text = lastError?.kind === 'auth'
        ? 'Sync failed · sign in to retry'
        : lastError?.kind === 'validation'
          ? 'Sync failed · server rejected'
          : 'Sync failed';
      sub = 'Tap retry to try again';
      tone = 'bg-red-50 text-red-700 border-red-200';
      break;
    case SAVE_STATUS.CLEAN:
    default:
      icon = <Cloud className="w-3.5 h-3.5 opacity-50" />;
      text = lastSavedAt ? 'Saved to cloud' : 'No changes';
      sub = lastSavedAt ? formatRelative(lastSavedAt) : '';
      tone = 'bg-gray-50 text-gray-600 border-gray-200';
      break;
  }

  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-medium border rounded-full px-2.5 py-1 ${tone}`}
      data-testid="save-status-badge"
      data-status={status}
    >
      {icon}
      <span data-testid="save-status-text">{text}</span>
      {sub && <span className="opacity-70 hidden sm:inline">· {sub}</span>}
      {showRetry && onRetry && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onRetry}
          className="h-6 px-2 text-xs font-semibold hover:bg-white/60"
          data-testid="save-status-retry"
        >
          Retry now
        </Button>
      )}
      {developerMode && status !== SAVE_STATUS.SAVED && status !== SAVE_STATUS.CLEAN && status !== SAVE_STATUS.SAVING && (
        <Link
          to="/sync-queue"
          className="text-xs font-medium underline opacity-70 hover:opacity-100"
          data-testid="save-status-details"
        >
          Details
        </Link>
      )}
    </div>
  );
};

export default SaveStatusBadge;
